import { getCompanyPreferences } from "@/services/CompanyPreferences.service";
import { CompanyPreferences } from "@/types";
import { timedOrdersFetch } from "@/utils/ordersPerfLog";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, MaybeRefOrGetter, toValue } from "vue";

const REFERENCE_STALE_MS = 5 * 60 * 1000;

export const COMPANY_PREFERENCES_QUERY_KEY = ["companyPreferences"] as const;

function toNumberOrUndefined(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function normalizeCompanyPreferences(preferences: CompanyPreferences): CompanyPreferences {
  return {
    ...preferences,
    blankSpaceSize: toNumberOrUndefined(preferences?.blankSpaceSize),
  };
}

export function companyPreferencesQueryOptions() {
  return {
    queryKey: COMPANY_PREFERENCES_QUERY_KEY,
    queryFn: () => timedOrdersFetch("companyPreferences", async () => normalizeCompanyPreferences(await getCompanyPreferences())),
    staleTime: REFERENCE_STALE_MS,
    refetchOnMount: false as const,
  };
}

export default function useCompanyPreferences(options?: { enabled?: MaybeRefOrGetter<boolean> }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery<CompanyPreferences>({
    ...companyPreferencesQueryOptions(),
    enabled: computed(() => (options?.enabled === undefined ? true : !!toValue(options.enabled))),
  });

  const companyPreferences = computed(() => data.value);

  function setCompanyPreferences(next: CompanyPreferences) {
    const normalized = normalizeCompanyPreferences(next);
    queryClient.setQueryData(COMPANY_PREFERENCES_QUERY_KEY, normalized);
    return normalized;
  }

  return { companyPreferences, isLoading, isError, error, refetch, setCompanyPreferences };
}
